import type { LogoCloudSectionContent } from "@site-factory/config";
import { SectionShell } from "../components/SectionShell";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { cn } from "../utils";
import { Icon, type IconName } from "../icons";

export interface LogoCloudSectionProps extends LogoCloudSectionContent {
  className?: string;
}

const iconFallbacks: IconName[] = [
  "lightning",
  "sparkles",
  "rocket",
  "mountain",
  "wave",
  "tool",
  "globe",
  "briefcase",
  "package",
  "satellite",
];
const gradients = [
  "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)",
  "linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)",
  "linear-gradient(135deg, #34d399 0%, #059669 100%)",
  "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
  "linear-gradient(135deg, #f472b6 0%, #ec4899 100%)",
];

export function LogoCloudSection({
  title,
  subtitle,
  logos,
  variant = "grid",
  columns = 4,
  className,
}: LogoCloudSectionProps) {
  const cols =
    columns === 3
      ? "sm:grid-cols-2 md:grid-cols-3"
      : columns === 4
      ? "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      : columns === 5
      ? "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
      : "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6";

  return (
    <SectionShell className={className} backgroundClassName="bg-surface">
      {(title || subtitle) && (
        <div className="text-center mb-10">
          {title && <SectionTitle>{title}</SectionTitle>}
          {subtitle && <SectionSubtitle className="mt-3">{subtitle}</SectionSubtitle>}
        </div>
      )}

      <div
        className={cn(
          "grid gap-4 md:gap-5 justify-items-center",
          variant === "grid" ? cols : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
        )}
      >
        {logos.map((logo, idx) => {
          const name = logo.alt || "Brand";
          const gradient = gradients[idx % gradients.length];
          const iconName = iconFallbacks[idx % iconFallbacks.length];

          return (
            <div
              key={idx}
              className="group inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[var(--color-surface-elevated,#f1f5f9)] text-[var(--color-text,#0f172a)] shadow-sm hover:shadow-md border border-[color:var(--color-text,#0f172a)]/10 transition"
              aria-label={name}
            >
              <div
                className="w-9 h-9 rounded-full text-white flex items-center justify-center text-sm font-semibold"
                style={{ background: gradient }}
                aria-hidden
              >
                <Icon name={iconName} className="h-4 w-4 text-white" />
              </div>
              <span
                className="text-sm font-semibold"
                style={{ color: "var(--color-text,#0f172a)", opacity: 0.85 }}
              >
                {name}
              </span>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}
